(() => {
  'use strict';

  const root = document.documentElement;
  const body = document.body;
  const drawer = document.querySelector('[data-cart-drawer]');
  const overlay = document.querySelector('[data-cart-overlay]');
  const drawerItems = document.querySelector('[data-cart-items]');
  const drawerFooter = document.querySelector('[data-cart-footer]');
  const subtotal = document.querySelector('[data-cart-subtotal]');
  const countBadges = document.querySelectorAll('[data-cart-count]');
  const toast = document.querySelector('[data-toast]');
  const routes = window.MayaTheme?.routes || {};

  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const formatMoney = (cents, currency = 'USD') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
        maximumFractionDigits: cents % 100 === 0 ? 0 : 2
      }).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
    }
  };

  const showToast = (message, isError = false) => {
    if (!toast) return;
    toast.textContent = message;
    toast.classList.toggle('is-error', isError);
    toast.classList.add('is-visible');
    window.clearTimeout(showToast.timeout);
    showToast.timeout = window.setTimeout(() => toast.classList.remove('is-visible'), 2600);
  };

  const openCart = () => {
    if (!drawer || !overlay) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    overlay.hidden = false;
    body.classList.add('cart-is-open');
    drawer.querySelector('[data-cart-close]')?.focus();
  };

  const closeCart = () => {
    if (!drawer || !overlay) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    overlay.hidden = true;
    body.classList.remove('cart-is-open');
  };

  const updateCount = (count) => {
    countBadges.forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count === 0;
    });
  };

  const lineMarkup = (item, currency) => {
    const image = item.image
      ? `<img src="${escapeHtml(item.image)}&width=220" alt="${escapeHtml(item.product_title)}" loading="lazy">`
      : '';
    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? `<small>${escapeHtml(item.variant_title)}</small>`
      : '';
    return `
      <div class="cart-drawer__line" data-cart-key="${escapeHtml(item.key)}">
        <a class="cart-drawer__line-image" href="${escapeHtml(item.url)}">${image}</a>
        <div>
          <a class="cart-drawer__line-title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
          ${variant}
          <div class="cart-drawer__line-price">${formatMoney(item.final_line_price, currency)}</div>
          <div class="cart-drawer__line-actions">
            <div class="cart-mini-quantity">
              <button type="button" data-cart-change="${escapeHtml(item.key)}" data-cart-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button>
              <span>${item.quantity}</span>
              <button type="button" data-cart-change="${escapeHtml(item.key)}" data-cart-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
            </div>
            <button class="cart-remove" type="button" data-cart-change="${escapeHtml(item.key)}" data-cart-quantity="0">Remove</button>
          </div>
        </div>
      </div>`;
  };

  const renderCart = (cart) => {
    updateCount(cart.item_count || 0);
    if (!drawerItems || !drawerFooter) return;
    if (!cart.items?.length) {
      drawerItems.innerHTML = '<p class="cart-drawer__empty">Your cart is waiting for something crunchy.</p>';
      drawerFooter.hidden = true;
      return;
    }
    drawerItems.innerHTML = cart.items.map((item) => lineMarkup(item, cart.currency || 'USD')).join('');
    subtotal.textContent = formatMoney(cart.total_price, cart.currency || 'USD');
    drawerFooter.hidden = false;
  };

  const fetchCart = async () => {
    const response = await fetch(routes.cartJson || '/cart.js', {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to load cart');
    const cart = await response.json();
    renderCart(cart);
    return cart;
  };

  const setLoading = (button, loading) => {
    if (!button) return;
    if (loading) {
      button.dataset.originalText = button.textContent;
      button.textContent = 'Adding…';
      button.disabled = true;
    } else {
      button.textContent = button.dataset.originalText || 'Add to cart';
      button.disabled = false;
    }
  };

  const addItem = async (variantId, quantity, button, title = 'Item') => {
    setLoading(button, true);
    try {
      const response = await fetch(routes.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: Number(quantity) || 1 }] })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Could not add this item');
      await fetchCart();
      openCart();
      showToast(`${title} added to cart`);
      document.dispatchEvent(new CustomEvent('shopify:cart:lines-update', { bubbles: true, detail: { source: 'maya-theme' } }));
    } catch (error) {
      console.error(error);
      showToast(error.message || 'Could not add this item', true);
    } finally {
      setLoading(button, false);
    }
  };

  const changeItem = async (key, quantity, button) => {
    button?.setAttribute('disabled', 'disabled');
    try {
      const response = await fetch(routes.cartChange || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: Number(quantity) })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'Could not update cart');
      renderCart(cart);
      document.dispatchEvent(new CustomEvent('shopify:cart:lines-update', { bubbles: true, detail: { source: 'maya-theme' } }));
    } catch (error) {
      console.error(error);
      showToast(error.message || 'Could not update cart', true);
    } finally {
      button?.removeAttribute('disabled');
    }
  };

  document.addEventListener('click', (event) => {
    const quantityButton = event.target.closest('[data-quantity-minus], [data-quantity-plus]');
    if (quantityButton) {
      const picker = quantityButton.closest('[data-quantity-picker]');
      const input = picker?.querySelector('[data-quantity-input]');
      if (!input) return;
      const current = Math.max(1, Number(input.value) || 1);
      input.value = quantityButton.hasAttribute('data-quantity-plus') ? current + 1 : Math.max(1, current - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const addButton = event.target.closest('.js-add-to-cart');
    if (addButton) {
      const picker = addButton.closest('.maya-product-card, .catalog-bundle-card')?.querySelector('[data-quantity-input]');
      addItem(addButton.dataset.variantId, picker?.value || 1, addButton, addButton.dataset.productTitle || 'Item');
      return;
    }

    const cartChange = event.target.closest('[data-cart-change]');
    if (cartChange) {
      changeItem(cartChange.dataset.cartChange, cartChange.dataset.cartQuantity, cartChange);
      return;
    }

    if (event.target.closest('[data-cart-open]')) {
      fetchCart().catch(console.error).finally(openCart);
      return;
    }
    if (event.target.closest('[data-cart-close]') || event.target.matches('[data-cart-overlay]')) {
      closeCart();
      return;
    }

    const mobileToggle = event.target.closest('[data-mobile-nav-toggle]');
    if (mobileToggle) {
      const nav = document.querySelector('[data-mobile-nav]');
      const next = !nav?.classList.contains('is-open');
      nav?.classList.toggle('is-open', next);
      mobileToggle.setAttribute('aria-expanded', String(next));
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const formData = new FormData(form);
    await addItem(formData.get('id'), formData.get('quantity') || 1, button, document.querySelector('.main-product-info h1')?.textContent.trim() || 'Item');
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeCart();
  });

  fetchCart().catch(() => {});
})();
